import { Product, UTSLocale } from "@smile2impress/attraction-constants";

import findLowestPrice from "utils/findLowestPrice";

/**
 * Placeholder format: {{TreatmentCaseCode:month}} or {{TreatmentCaseCode:total}}
 * - TreatmentCaseCode: product/case code (e.g. LightCase, ModerateCase) passed to findLowestPrice productsFilter
 * - month = monthly price, total = one-time price
 */
const PLACEHOLDER_REGEX = /\{\{([^:}+]+):(month|total)\}\}/g;

export type ReplaceTreatmentCasePlaceholdersOptions = {
    products: Product[];
    locale: UTSLocale;
};

/**
 * Replaces {{TreatmentCaseCode:month}} and {{TreatmentCaseCode:total}} in a string
 * with the lowest price for that case (monthly or total).
 * Use for CMS text fields that should show dynamic prices (e.g. MainBannerExtra advantages).
 *
 * @param text - Raw string possibly containing placeholders
 * @param options - products and locale; if missing or products empty, placeholders are left unchanged
 * @returns String with placeholders replaced by formatted price, or original if no match / no products
 */
export function replaceTreatmentCasePlaceholders(
    text: string,
    options: ReplaceTreatmentCasePlaceholdersOptions | null | undefined
): string {
    if (!text || typeof text !== "string") return text;
    if (!options?.products?.length || !options.locale) return text;

    return text.replace(
        PLACEHOLDER_REGEX,
        (_, caseCode: string, kind: string) => {
            const monthly = kind === "month";
            const price = findLowestPrice({
                products: options.products,
                locale: options.locale,
                monthly,
                productsFilter: [caseCode.trim()],
            });
            return price != null ? String(price) : "";
        }
    );
}
